import { GATEWAY_TIMEOUT } from '@mm/constants/status-codes';
import LiveChatCRUDActions from '@mm/state/actions/LiveChatCRUDActions';
import {
  uploadFile,
  uploadImage,
  uploadVideo,
} from '@mm/state/actions/uploadActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { getErrorText } from '@mm/state/errorHandlers/common-handler';
import {
  DialogueMessagePayload,
  ExtendedContactMessage,
  MediaMessagePayload,
  ReactionMessagePayload,
  TextMessagePayload,
} from '@mm/state/model/ContactMessageModel';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useActor } from '@xstate/react';
import { ReactComponent as AttachmentIcon } from 'assets/attachment-icon.svg';
import errorIcon from 'assets/baseline-error_outline.svg';
import classnames from 'classnames';
import emojiRegex from 'emoji-regex';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { useContextSelector } from 'use-context-selector';
import { ErrorTooltip } from '../../../../../../common/components/ErrorTooltip/ErrorTooltip';
import { Linkify } from '../../../../../../common/components/Linkify';
import { getFormatFromType } from '../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { PersonaContext } from '../../../HelperContexts';
import { LiveChatContext } from '../../../LiveChatContext';
import { useSendMessage } from '../../MessageSender/MessageSender';
import { ProfileImage } from '../../ProfileImage/ProfileImage';
import { ImageMessage } from '../ImageMessage/ImageMessage';
import './UnsentMessage.pcss';

const EMOJI_REGEXP = emojiRegex();
const wellKnownMessages = [
  [
    /person isn't available/,
    'This contact is not accepting messages from your page at the moment.',
  ],
  [
    /privacy rules in Europe/,
    'Responses with a Persona are not allowed due to new privacy policies in Europe. Please resend the message with no persona selected.',
  ],
  [
    /This message is sent outside of allowed window/,
    'This message is sent outside of allowed window',
  ],
];

type Props = {
  message: ExtendedContactMessage;
};

const SMS_EXCLUDE_TYPES = [
  WidgetTypes.OTN,
  WidgetTypes.TYPING,
  WidgetTypes.ATTACHMENT,
  WidgetTypes.GALLERY,
];
const IG_EXCLUDE_TYPES = [
  WidgetTypes.OTN,
  WidgetTypes.TYPING,
  WidgetTypes.ATTACHMENT,
  WidgetTypes.GALLERY,
  WidgetTypes.VIDEO,
  WidgetTypes.OPT_IN,
];
const WC_EXCLUDE_TYPES = [WidgetTypes.OTN];

const filteredWidgets = (widgets: WidgetModel[], userId: string) => {
  if (userId.startsWith('+')) {
    return widgets.filter(
      w =>
        SMS_EXCLUDE_TYPES.indexOf(w.type) === -1 && w.supportedByPlatforms.sms,
    );
  }
  if (userId.startsWith('ig_')) {
    return widgets.filter(
      w =>
        IG_EXCLUDE_TYPES.indexOf(w.type) === -1 &&
        w.supportedByPlatforms.instagram,
    );
  }
  if (userId.startsWith('wc_')) {
    return widgets.filter(
      w =>
        WC_EXCLUDE_TYPES.indexOf(w.type) === -1 &&
        w.supportedByPlatforms.webChat,
    );
  }
  return widgets;
};

const Reaction = () => {
  return (
    <>
      <span>{'\u{2764}\u{FE0F}'}</span>️
    </>
  );
};

const Media = ({ message }: { message: MediaMessagePayload }) => {
  const { file } = message;
  const type = file['type'].split('/')[0];
  const isImage = type === 'image';
  const isVideo = type === 'video';
  const url = URL.createObjectURL(file);
  if (isImage) {
    return <ImageMessage url={url} />;
  }
  if (isVideo) {
    return (
      <div className="unsent-message__body-video-container">
        <video
          controls
          muted
          autoPlay
          loop
          className="unsent-message__body-video"
        >
          <source src={url} />
        </video>
      </div>
    );
  }
  return (
    <div className="unsent-message__attachment-preview">
      <AttachmentIcon />
      <div className="unsent-message__attachment-file-name">{file.name}</div>
    </div>
  );
};

const Dialogue = ({
  message,
  error,
  canResend,
  resend,
}: {
  message: DialogueMessagePayload;
  canResend: boolean;
  resend: () => void;
  error?: string;
}) => {
  return (
    <div className="unsent-message__bot-reply">
      <div className="unsent-message__bot-reply-text">
        {error && renderError(error)}
        <span>
          <b>
            {error
              ? 'Dialogue failed to send'
              : 'Dialogue is being sent via Live Chat'}
            :
          </b>{' '}
          {message.dialogueName}
          {error && (
            <>
              {' -'}
              {renderResendButton(canResend, resend)}
            </>
          )}
        </span>
      </div>
    </div>
  );
};

const renderResendButton = (canResend: boolean, resend: () => void) => {
  const classes = classnames('unsent-message__resend-button', {
    'unsent-message__resend-button--disabled': !canResend,
  });
  return (
    <span className={classes} onClick={resend}>
      Try again
    </span>
  );
};

const Text = ({ message }: { message: TextMessagePayload }) => {
  return <Linkify>{message.text}</Linkify>;
};

const renderError = (error: string, iconText?: string) => {
  const wellKnownError = wellKnownMessages.find(([exp, _message]) =>
    error.match(exp),
  );
  return (
    <ErrorTooltip
      classPrefix="unsent-message"
      type="error"
      align="start"
      hoverMode={true}
      customIcon={
        <span>
          <img src={errorIcon} alt="" className="unsent-message__error-icon" />
          {iconText}
        </span>
      }
      customMessage={
        <div className="unsent-message__tooltip-error">
          <div className="unsent-message__tooltip-error-detection-label">
            <b>Error Detected:</b>
          </div>
          <div className="unsent-message__tooltip-error-text">
            {wellKnownError?.[1] || error}
          </div>
        </div>
      }
    />
  );
};

export const UnsentMessage = (props: Props) => {
  const { message } = props;

  const remoteFbPageId = useSelector(
    (state: StoreState) => state.connectedPage.pageDetails?.remoteId || '',
  );

  const currentPersona = useContextSelector(
    PersonaContext,
    v => v.currentPersona,
  );

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const { remoteUserId } = lcMachineState.context || {};

  const conversationMachine = remoteUserId
    ? lcMachineState.context.conversationsRefs[remoteUserId]
    : undefined;

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const refreshSelectedContact = useCallback(() => {
    selectedContactMachine?.send({
      type: 'LC_CONTACT_REFRESH',
      payload: remoteUserId || '',
    });
  }, [selectedContactMachine?.id, remoteUserId]);

  const { dialogueId } = message.message as DialogueMessagePayload;
  const widgetCRUDActions = useWidgetCRUDActions();
  const widgets = useSelector((state: StoreState) => {
    if (dialogueId) {
      return widgetsByDialogueSelector(dialogueId, state.widget.entities);
    }
    return [];
  });
  const [isResending, setIsResending] = useState(false);
  const [timedOutAt, setTimedOutAt] = useState<Date | undefined>();
  const [timeoutId, setTimeoutId] = useState<
    ReturnType<typeof setTimeout> | undefined
  >();
  const dispatch: Dispatch<StoreState> = useDispatch();
  const { showWarning, usePersona } = useSendMessage(currentPersona);
  const localPersonaId = usePersona ? currentPersona?.id : undefined;
  const canResend = showWarning !== '7d';
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const avatar = activeFacebookPage?.squarePicture;
  const isLike = (message.message as ReactionMessagePayload).reaction;
  const isMedia = (message.message as MediaMessagePayload).file;
  const isDialogue = (message.message as DialogueMessagePayload).dialogueId;
  const isText = (message.message as TextMessagePayload).text;
  const messageError = message.error;
  // timeout for media - 5 minutes, for anything else - 30 seconds
  const timeOut = isMedia ? 300000 : 30000;
  const timeoutError = moment(Number(message.time)).isBefore(
    moment().subtract(timeOut, 'ms'),
  )
    ? 'request timed out'
    : undefined;
  const error = messageError || timeoutError;

  const showError = !isResending && error;
  const isEmojiOnly =
    isText &&
    (message.message as TextMessagePayload).text.replace(EMOJI_REGEXP, '')
      .length === 0;
  const messageClass = classnames('unsent-message', {
    'unsent-message--emoji-only': isLike || isEmojiOnly,
    'unsent-message--error': showError,
  });
  useEffect(() => {
    if (dialogueId && widgets.length === 0) {
      widgetCRUDActions.retrieveAll(dialogueId);
    }
  }, []);
  useEffect(() => {
    if (isResending || error) {
      return;
    }
    const diff = Number(message.time) + timeOut + 5000 - new Date().valueOf();
    const localTimeoutId = setTimeout(() => {
      setTimedOutAt(new Date());
    }, Math.max(0, diff));
    setTimeoutId(localTimeoutId);
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isResending]);
  const resendMessage = async (
    text: string,
    personaId?: number,
    dialogueParams?: DialogueMessagePayload,
    likeHeart?: boolean,
  ) => {
    if (remoteFbPageId) {
      const result = await dispatch(
        LiveChatCRUDActions.sendMessage(
          remoteFbPageId as string,
          message.remoteUserId,
          text,
          personaId,
          dialogueParams?.dialogueId,
          likeHeart,
          message.clientMessageId,
        ),
      );
      if (conversationMachine?.state.context.model.inboxStatus !== 'main') {
        conversationMachine?.send({
          type: 'LC_CONVERSATION_REDOWNLOAD',
        });
        lcMachineSend({
          type: 'LC_CONVERSATIONS_REFILTER',
          payload: [message.remoteUserId],
        });
      }
      if (result?.messageId) {
        conversationMachine?.send({
          type: 'LC_UPDATE_OPTIMISTIC_MESSAGE',
          payload: {
            ...message,
            error: undefined,
            time: new Date(),
            lcMessageId: result.messageId,
          },
        });
      }
    }
    return;
  };
  const resendMedia = async (
    media: MediaMessagePayload,
    localPersonaId?: number,
  ) => {
    const { file } = media;
    let { assetId } = media;
    let updatedMessage = { ...message, time: new Date() };
    const getUploadMethod = () => {
      if (file.type.startsWith('image/')) {
        return uploadImage;
      }
      if (file.type.startsWith('video/')) {
        return uploadVideo;
      }
      return uploadFile;
    };
    if (!assetId) {
      assetId = await dispatch(
        getUploadMethod()({
          content: file,
          extension: getFormatFromType(file.type),
        }),
      );
      updatedMessage = {
        ...message,
        time: new Date(Number(message.time)),
        message: { file, assetId },
      };

      conversationMachine?.send({
        type: 'LC_UPDATE_OPTIMISTIC_MESSAGE',
        payload: updatedMessage,
      });
    }
    const result = await dispatch(
      LiveChatCRUDActions.sendAsset(
        remoteFbPageId as string,
        message.remoteUserId,
        assetId as number,
        localPersonaId,
        message.clientMessageId,
      ),
    );
    if (conversationMachine?.state.context.model.inboxStatus !== 'main') {
      conversationMachine?.send({
        type: 'LC_CONVERSATION_REDOWNLOAD',
      });

      lcMachineSend({
        type: 'LC_CONVERSATIONS_REFILTER',
        payload: [message.remoteUserId],
      });
    }
    if (result?.messageId) {
      conversationMachine?.send({
        type: 'LC_UPDATE_OPTIMISTIC_MESSAGE',
        payload: {
          ...updatedMessage,
          error: undefined,
          time: new Date(),
          lcMessageId: result.messageId,
        },
      });
    }
    return;
  };
  const resend = async () => {
    if (!canResend) {
      return;
    }
    setIsResending(true);
    try {
      if (isMedia) {
        await resendMedia(
          message.message as MediaMessagePayload,
          localPersonaId,
        );
      } else if (isLike) {
        await resendMessage('', localPersonaId, undefined, true);
      } else if (isText) {
        await resendMessage(
          (message.message as TextMessagePayload).text,
          localPersonaId,
          undefined,
          false,
        );
      } else {
        await resendMessage(
          '',
          localPersonaId,
          message.message as DialogueMessagePayload,
          false,
        );
      }
    } catch (e) {
      if (isMedia && e instanceof Response && e.status === GATEWAY_TIMEOUT) {
        // 504 for media usually refers to Nginx gateway timeouts
        return;
      }
      const errorMessage = await getErrorText(e);
      conversationMachine?.send({
        type: 'LC_UPDATE_OPTIMISTIC_MESSAGE',
        payload: {
          ...message,
          time: new Date(Number(message.time)),
          error: errorMessage,
        },
      });
    } finally {
      setIsResending(false);
      refreshSelectedContact();
    }
  };
  if (
    isDialogue &&
    message.lcMessageId &&
    filteredWidgets(widgets, message.remoteUserId).length === 0
  ) {
    return null;
  }
  return (
    <div className="unsent-message__main-container">
      {isDialogue && (
        <Dialogue
          message={message.message as DialogueMessagePayload}
          error={isResending ? undefined : error}
          canResend={canResend}
          resend={resend}
        />
      )}
      {!isDialogue && (
        <>
          <div className="unsent-message__container">
            <div className="unsent-message__timestamp">
              {moment(Number(message.time)).format('YYYY-MM-DD HH:mm')}
            </div>
            <div className="unsent-message__message">
              <div className={messageClass}>
                <div className="unsent-message__body">
                  {isLike && <Reaction />}
                  {isMedia && (
                    <Media message={message.message as MediaMessagePayload} />
                  )}
                  {isText && (
                    <Text message={message.message as TextMessagePayload} />
                  )}
                </div>
              </div>
            </div>
            {avatar ? (
              <ProfileImage altText={''} profilePictureUrl={avatar} big />
            ) : null}
          </div>
          {showError && (
            <div className="unsent-message__resend">
              {renderError(error as string, ' Message could not be sent.')}
              {renderResendButton(canResend, resend)}
            </div>
          )}
        </>
      )}
    </div>
  );
};
